import { z } from 'zod';
import { formatCents } from '../types/money.js';
/**
 * Punkte-Aufgaben-Tool (ADR-039). 1 Punkt = 1 Cent. Auszahlung ab 1.000 P als
 * Umbuchung ins Taschengeld. Aufgaben-Instanzen leben als Listen-Items in
 * „Zu erledigen"; hier die Vorlagen- und Konto-Contracts.
 */
/** Auszahlungsschwelle in Punkten (1.000 P = 10 €). */
export const POINTS_PAYOUT_THRESHOLD = 1000;
/** Anzeige „50 P". */
export const formatPoints = (p) => `${p} P`;
/** Euro-Gegenwert eines Punktestands (1 P = 1 Cent). */
export const pointsToEuro = (p) => formatCents(p);
export const POINTS_TXN_TYPES = ['credit_task', 'payout', 'adjustment'];
export const TASK_RECURRENCES = ['once', 'daily', 'weekly', 'monthly'];
// ── Punktekonto ───────────────────────────────────────────────────
export const pointsAccountDtoSchema = z.object({
    id: z.string().uuid(),
    memberId: z.string().uuid(),
    memberName: z.string(),
    balance: z.number().int(), // Punkte
    canPayout: z.boolean(), // balance >= Schwelle
    threshold: z.number().int(),
});
export const pointsTxnDtoSchema = z.object({
    id: z.string().uuid(),
    amount: z.number().int(), // signiert
    type: z.enum(POINTS_TXN_TYPES),
    note: z.string().nullable(),
    relatedListItemId: z.string().uuid().nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string(),
});
// ── Aufgaben-Vorlage ──────────────────────────────────────────────
export const taskTemplateDtoSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    points: z.number().int(),
    recurrence: z.enum(TASK_RECURRENCES),
    daysOfWeek: z.array(z.number().int()), // 0–6 (So–Sa), „mehrmals pro Woche"
    daysOfMonth: z.array(z.number().int()), // 1–31, „mehrmals pro Monat"
    dueTime: z.string().nullable(),
    dueAt: z.string().nullable(),
    assigneeMemberId: z.string().uuid().nullable(),
    perChild: z.boolean(), // Jedes Kind für sich (eigene Instanz je Kind)
    untilDate: z.string().nullable(),
    active: z.boolean(),
    nextRunAt: z.string().nullable(),
});
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit als HH:MM');
const isoDateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als YYYY-MM-DD');
const daysOfWeek = z.array(z.number().int().min(0).max(6)).max(7);
const daysOfMonth = z.array(z.number().int().min(1).max(31)).max(31);
export const createTaskTemplateSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    points: z.number().int().min(0).max(100000),
    recurrence: z.enum(TASK_RECURRENCES),
    daysOfWeek: daysOfWeek.optional(), // weekly: ein oder mehrere Wochentage
    daysOfMonth: daysOfMonth.optional(), // monthly: ein oder mehrere Tage
    dueTime: hhmm.nullable().optional(), // wiederkehrend: „bis"-Uhrzeit
    dueAt: z.string().nullable().optional(), // once: absolutes Fälligkeitsdatum (ISO)
    assigneeMemberId: z.string().uuid().nullable().optional(), // null = offen für alle Kinder
    perChild: z.boolean().optional(), // Jedes Kind für sich
    untilDate: isoDateStr.nullable().optional(), // Serienende
})
    .refine((d) => d.recurrence !== 'weekly' || (d.daysOfWeek?.length ?? 0) > 0, { message: 'Mindestens einen Wochentag wählen', path: ['daysOfWeek'] })
    .refine((d) => d.recurrence !== 'monthly' || (d.daysOfMonth?.length ?? 0) > 0, { message: 'Mindestens einen Tag im Monat wählen', path: ['daysOfMonth'] });
export const updateTaskTemplateSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    points: z.number().int().min(0).max(100000).optional(),
    daysOfWeek: daysOfWeek.optional(),
    daysOfMonth: daysOfMonth.optional(),
    dueTime: hhmm.nullable().optional(),
    dueAt: z.string().nullable().optional(),
    assigneeMemberId: z.string().uuid().nullable().optional(),
    perChild: z.boolean().optional(),
    untilDate: isoDateStr.nullable().optional(),
    active: z.boolean().optional(),
});
// Aufgabe nachtragen (ADR-077): Eltern schreiben einem Kind eine bereits erledigte
// Aufgabe direkt gut — legt ein sofort bestätigtes list_item an + Punkte + Feed.
export const creditTaskSchema = z.object({
    childId: z.string().uuid(),
    title: z.string().trim().min(1).max(200),
    points: z.number().int().min(0).max(100000),
});
//# sourceMappingURL=tasks.js.map